import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder, faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import Button from 'src/components/Button'
import { Card } from 'src/components/Card'
import { CreateProjectModal } from 'src/components/ProjectsWidget/CreateProjectModal'
import { PROJECTS_ROUTE } from 'src/constants/routes'
import { BaseModal } from 'src/containers/BaseModal'
import styles from './projectsPlaceholderModal.module.scss'

interface ProjectsPlaceholderModalProps {
  isOpen: boolean
  onClose: () => void
  onProjectCreated?: () => void
}

export const ProjectsPlaceholderModal: FC<ProjectsPlaceholderModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated,
}) => {
  const navigate = useNavigate()
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState<boolean>(false)
  // Latch: once a project is created, keep the placeholder card closed so it
  // can't pop back up when the create form closes. Reset when the placeholder
  // is fully dismissed.
  const [hasCreated, setHasCreated] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      setHasCreated(false)
      setIsCreateProjectModalOpen(false)
    }
  }, [isOpen])

  const handleCreateProjectModalClose = () => {
    setIsCreateProjectModalOpen(false)
  }

  const handleProjectCreated = () => {
    setIsCreateProjectModalOpen(false)
    setHasCreated(true)
    if (onProjectCreated) {
      onProjectCreated()
      return
    }

    navigate(PROJECTS_ROUTE)
  }

  return (
    <>
      <BaseModal
        isOpen={isOpen && !isCreateProjectModalOpen && !hasCreated}
        onRequestClose={onClose}
      >
        <Card className={styles.modalCard}>
          <div className={styles.header}>
            <h1 className={styles.headerTitle}>Create New Project</h1>

            <button
              aria-label="Close modal"
              className={styles.closeButton}
              onClick={onClose}
              type="button"
            >
              ×
            </button>
          </div>

          <div className={styles.body}>
            <div className={styles.folderIconBox}>
              <FontAwesomeIcon className={styles.folderIcon} icon={faFolder} />
            </div>

            <div className={styles.content}>
              <h2 className={styles.title}>No projects created yet</h2>
              <p className={styles.description}>
                Create your first project to start tracking campaigns,
                sentiment, analytics, and insights.
              </p>
            </div>

            <Button
              className={styles.primaryButton}
              onClick={() => setIsCreateProjectModalOpen(true)}
            >
              <span className={styles.buttonContent}>
                <FontAwesomeIcon
                  className={styles.buttonIcon}
                  icon={faPlusCircle}
                />
                <span>Create project</span>
              </span>
            </Button>
          </div>
        </Card>
      </BaseModal>

      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={handleCreateProjectModalClose}
        onSuccess={handleProjectCreated}
      />
    </>
  )
}
